import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../services/common.service';
import { API_URLS } from '../../constants/api-urls';
import './InspectionRFQDetails.scss';

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function QARequestQuotationDetail() {
  const { qaRequestId } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    quote_amount: 5000,
    currency: 'INR',
    proposed_start_date: '',
    proposed_end_date: '',
    proposed_time: '10:00',
    estimated_duration_hours: '2-3 hours',
    inspection_approach: '',
    additional_notes: '',
  });

  useEffect(() => {
    fetchRequest();
  }, [qaRequestId]);

  const fetchRequest = async () => {
    try {
      setLoading(true);
      const response = await CommonService.get(API_URLS.QA.GET_BY_ID(qaRequestId));
      if (response && response.success && response.data) {
        const data = response.data;
        setRequest(data);
        if (data.preferred_start_date) {
          const start = new Date(data.preferred_start_date);
          setFormData(prev => ({
            ...prev,
            proposed_start_date: start.toISOString().split('T')[0],
            proposed_time: (data.preferred_time || '10:00').toString().substring(0, 5),
          }));
          if (data.preferred_end_date) {
            const end = new Date(data.preferred_end_date);
            setFormData(prev => ({ ...prev, proposed_end_date: end.toISOString().split('T')[0] }));
          }
        }
      } else {
        setRequest(null);
      }
    } catch (error) {
      console.error('Error fetching QA request:', error);
      toast.error('Failed to load request');
      setRequest(null);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!request) return;
    if (!formData.quote_amount || formData.quote_amount < 1) {
      toast.error('Please enter a valid quote amount');
      return;
    }
    if (!formData.inspection_approach || formData.inspection_approach.trim().length < 20) {
      toast.error('Inspection approach must be at least 20 characters');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        qa_request_id: parseInt(qaRequestId, 10),
        quote_amount: parseFloat(formData.quote_amount),
        currency: formData.currency || 'INR',
        proposed_start_date: formData.proposed_start_date || undefined,
        proposed_end_date: formData.proposed_end_date || undefined,
        proposed_time: formData.proposed_time || undefined,
        estimated_duration_hours: formData.estimated_duration_hours || undefined,
        inspection_approach: formData.inspection_approach.trim(),
        additional_notes: (formData.additional_notes || '').trim() || undefined,
      };
      const response = await CommonService.post(API_URLS.QA.QUOTATION_SUBMIT, payload);
      if (response && response.success) {
        toast.success('Quotation submitted successfully');
        navigate('/auction/qa/dashboard');
      } else {
        throw new Error(response?.message || 'Failed to submit quotation');
      }
    } catch (error) {
      console.error('Error submitting quotation:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to submit quotation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="inspection-rfq-details loading">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="inspection-rfq-details error">
        <p>QA request not found.</p>
        <button className="back-btn" onClick={() => navigate('/auction/qa/dashboard')}>Back to Dashboard</button>
      </div>
    );
  }

  const scope = request.scope_logistics || {};
  const productName = scope.product_name || 'Quality Inspection Request';

  return (
    <div className="inspection-rfq-details">
      <div className="details-container">
        <button className="back-btn" onClick={() => navigate('/auction/qa/dashboard/requests')}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back to Requests
        </button>

        <div className="details-layout">
          <div className="left-column">
            <div className="section-card">
              <div className="section-header">
                <h2>{request.qa_request_number || `QA #${request.qa_request_id}`}</h2>
              </div>
              <h3 className="product-name">{productName}</h3>
              <div className="detail-row">
                <span className="detail-label">Location</span>
                <span className="detail-value">
                  {request.inspection_location_address || '—'}<br />
                  {[request.inspection_location_city, request.inspection_location_state, request.inspection_location_pincode].filter(Boolean).join(', ') || '—'}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Preferred schedule</span>
                <span className="detail-value">
                  {request.preferred_start_date ? formatDate(request.preferred_start_date) : '—'}
                  {request.preferred_end_date ? ` to ${formatDate(request.preferred_end_date)}` : ''}
                </span>
              </div>
              {scope.detailed_inspection_instructions && (
                <div className="detail-row">
                  <span className="detail-label">Instructions</span>
                  <span className="detail-value">{scope.detailed_inspection_instructions}</span>
                </div>
              )}
            </div>
          </div>

          <div className="right-column">
            <form onSubmit={handleSubmit} className="quotation-form-card">
              <div className="section-header">
                <h3>Submit Your Quotation</h3>
              </div>
              <div className="form-group">
                <label>Quote Amount (₹) <span className="required">*</span></label>
                <div className="input-with-prefix">
                  <span className="prefix">₹</span>
                  <input
                    type="number"
                    className="form-control"
                    name="quote_amount"
                    value={formData.quote_amount}
                    onChange={handleInputChange}
                    min="1"
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Proposed Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="proposed_start_date"
                  value={formData.proposed_start_date}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Proposed End Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="proposed_end_date"
                  value={formData.proposed_end_date}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Proposed Time</label>
                <input
                  type="time"
                  className="form-control"
                  name="proposed_time"
                  value={formData.proposed_time}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label>Estimated Duration</label>
                <select
                  className="form-control"
                  name="estimated_duration_hours"
                  value={formData.estimated_duration_hours}
                  onChange={handleInputChange}
                >
                  <option value="1-2 hours">1-2 hours</option>
                  <option value="2-3 hours">2-3 hours</option>
                  <option value="3-4 hours">3-4 hours</option>
                  <option value="4-6 hours">4-6 hours</option>
                  <option value="6+ hours">6+ hours</option>
                </select>
              </div>
              <div className="form-group">
                <label>Inspection Approach <span className="required">*</span></label>
                <textarea
                  className="form-control"
                  name="inspection_approach"
                  rows="5"
                  placeholder="Describe your inspection methodology and approach..."
                  value={formData.inspection_approach}
                  onChange={handleInputChange}
                  required
                  minLength={20}
                />
                <div className="form-hint">Minimum 20 characters</div>
              </div>
              <div className="form-group">
                <label>Additional Notes</label>
                <textarea
                  className="form-control"
                  name="additional_notes"
                  rows="3"
                  placeholder="Any additional information..."
                  value={formData.additional_notes}
                  onChange={handleInputChange}
                />
              </div>
              <button type="submit" className="btn-submit" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Quotation'}
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M18 2L9 11M18 2L12 18L9 11M18 2L2 8L9 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
